/**
 * Schreibweisen von Adressen (PV-D02).
 *
 * Strassennamen werden uneinheitlich geschrieben — "Puchheim Gasse" und
 * "Puchheimgasse" meinen dieselbe Gasse, und welche Fassung im Verzeichnis
 * steht, weiss niemand vorher. Wer nur die eingetippte Fassung sucht, bekommt
 * "Keine Adresse gefunden", obwohl die Adresse existiert. Danach steht das
 * Projekt ohne Standort da, und ohne Standort laedt das Vorbereiten nichts und
 * der Gebaeudevorschlag findet nichts. Eine Schreibweise haelt also den ganzen
 * Ablauf auf.
 *
 * Deshalb wird nicht eine Fassung gesucht, sondern der Reihe nach mehrere.
 * Reine Textarbeit, ohne Netz — deshalb hier und mit Test.
 */

/** Grundwoerter, die getrennt oder zusammen geschrieben vorkommen. */
const GRUNDWOERTER = [
  'gasse',
  'strasse',
  'straße',
  'weg',
  'platz',
  'ring',
  'allee',
  'zeile',
  'steig',
  'berg',
  'feld',
];

const aufgeraeumt = (wert: string): string => wert.trim().replace(/\s+/g, ' ');

/**
 * Obergrenze fuer die Zahl der Schreibweisen. Jede davon ist eine Abfrage beim
 * Adressdienst, und der laesst eine je Sekunde zu — wer zwanzig Fassungen
 * durchprobiert, wartet zwanzig Sekunden auf ein "nicht gefunden".
 */
const HOECHSTENS = 8;

/**
 * Die Schreibweisen, die zu einer Eingabe gesucht werden — die eingetippte
 * zuerst, denn die ist am ehesten gemeint.
 */
export function adressvarianten(eingabe: string): string[] {
  const start = aufgeraeumt(eingabe);
  if (!start) return [];

  const gefunden: string[] = [];
  const merke = (wert: string): void => {
    const sauber = aufgeraeumt(wert);
    if (sauber && !gefunden.includes(sauber) && gefunden.length < HOECHSTENS) {
      gefunden.push(sauber);
    }
  };
  merke(start);

  // Beim Tauschen die Gross- und Kleinschreibung mitnehmen, sonst steht da
  // "Haupt strasse".
  const wieGeschrieben = (vorlage: string, ersatz: string): string =>
    vorlage.charAt(0) === vorlage.charAt(0).toUpperCase()
      ? ersatz.charAt(0).toUpperCase() + ersatz.slice(1)
      : ersatz;

  // Die Schritte bauen aufeinander auf, deshalb laeuft jeder ueber alles, was
  // bisher entstanden ist. Frueher lief die Abkuerzung zuletzt und nur ueber
  // sich selbst — "Bahnhof Str. 7" wurde zu "Bahnhof Straße 7", und die
  // Fassung mit ss kam nie zustande. Steht sie so im Verzeichnis, findet die
  // Suche nichts.

  // 1. "Str." und "Str" sind gaengige Abkuerzungen — der Punkt gehoert mit weg.
  for (const fassung of [...gefunden]) {
    if (/\bstr\.?(?=\s|,|$)/i.test(fassung)) {
      merke(fassung.replace(/\bstr\.?(?=\s|,|$)/gi, (t) => wieGeschrieben(t, 'Straße')));
    }
    const angehaengt = /(\p{L}{2,}?)str\.?(?=\s|,|$)/giu;
    if (angehaengt.test(fassung)) merke(fassung.replace(angehaengt, '$1straße'));
  }

  // 2. Getrennt oder zusammen geschrieben.
  for (const fassung of [...gefunden]) {
    for (const grundwort of GRUNDWOERTER) {
      // "Puchheim Gasse" -> "Puchheimgasse"
      const getrennt = new RegExp(`(\\p{L})\\s+(${grundwort})\\b`, 'giu');
      if (getrennt.test(fassung)) merke(fassung.replace(getrennt, `$1${grundwort}`));

      // "Puchheimgasse" -> "Puchheim Gasse", aber nur, wenn davor noch etwas
      // steht. Sonst wuerde aus "Gasse" ein leeres Wort plus "Gasse".
      const zusammen = new RegExp(`(\\p{L}{2,})(${grundwort})\\b`, 'giu');
      if (zusammen.test(fassung)) {
        merke(
          fassung.replace(zusammen, (_treffer, vorne: string, wort: string) => {
            const grossgeschrieben = wort.charAt(0).toUpperCase() + wort.slice(1);
            return `${vorne} ${grossgeschrieben}`;
          }),
        );
      }
    }
  }

  // 3. ß und ss sind dieselbe Strasse — aber nur in "Strasse". Wer jedes ss
  //    tauscht, macht aus einer Gasse eine "Gaße".
  for (const fassung of [...gefunden]) {
    if (/stra(ß|ss)e/i.test(fassung)) {
      merke(fassung.replace(/straße/gi, (t) => wieGeschrieben(t, 'strasse')));
      merke(fassung.replace(/strasse/gi, (t) => wieGeschrieben(t, 'straße')));
    }
  }

  return gefunden;
}
